import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { buildEmailHTML, DEFAULT_REPLY_EMAIL } from "./utils/emailHtml";
import { emailCampaignsApi } from "./api/emailCampaignsApi";

const DEFAULT_OFFICE = DEFAULT_REPLY_EMAIL;

function resolveReplyEmail(mode, customEmail, myEmail) {
  if (mode === "my_email") return myEmail || DEFAULT_OFFICE;
  if (mode === "other")    return (customEmail || "").trim() || DEFAULT_OFFICE;
  return DEFAULT_OFFICE;
}

// Strip the full HTML document down to the inner body so it renders inline.
function innerBody(html) {
  return String(html || "")
    .replace(/<!doctype html>[\s\S]*?<body[^>]*>/i, "")
    .replace(/<\/body>[\s\S]*?<\/html>/i, "")
    .trim();
}

export default function CampaignConfirmation({
  subject,
  bodyHtml,
  signature,
  replyToMode,
  replyToCustomEmail,
}) {
  const replyToEmail = resolveReplyEmail(replyToMode, replyToCustomEmail, signature?.email);
  const senderName   = signature?.senderName || "Abisiniya Travels";
  const senderEmail  = signature?.email      || DEFAULT_OFFICE;

  // Build the exact HTML the campaign will store (same call the send path uses).
  const fullHtml = useMemo(
    () =>
      buildEmailHTML({
        subject,
        bodyHtml,
        signature: signature ? { ...signature, enabled: true } : { enabled: false },
        replyToEmail,
      }),
    [subject, bodyHtml, signature, replyToEmail]
  );

  // Ask the server to render it through the SAME pipeline used to send (agent
  // image → company image → suppress-when-signed-off → text), so this preview is
  // byte-identical to the delivered email. The server returns the signature
  // embedded as a data: URI so the real (cid:) image is viewable here.
  const [previewHtml, setPreviewHtml] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await emailCampaignsApi.preview({ subject, body_html: fullHtml });
        if (cancelled) return;
        setPreviewHtml(innerBody(data?.html) || innerBody(fullHtml));
      } catch {
        // Network/permission issue — fall back to the locally-built HTML so the
        // user still sees the body (signature may differ from the actual send).
        if (!cancelled) setPreviewHtml(innerBody(fullHtml));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subject, fullHtml]);

  return (
    <Box>
      {/* Email meta header */}
      <Box sx={{ pb: 2, mb: 2, borderBottom: "1px solid #e8e8e8" }}>
        <Typography sx={{ fontSize: 10, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.5 }}>
          Subject
        </Typography>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#111", lineHeight: 1.3 }}>
          {subject || "(No subject)"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", mt: 1 }}>
          <Typography sx={{ fontSize: 12, color: "#aaa", minWidth: 40 }}>From:</Typography>
          <Typography sx={{ fontSize: 12, color: "#333" }}>{senderName} &lt;{senderEmail}&gt;</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", mt: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: "#aaa", minWidth: 40 }}>To:</Typography>
          <Typography sx={{ fontSize: 12, color: "#333" }}>Your campaign subscribers</Typography>
        </Box>
      </Box>

      {/* Email body — server-rendered so the signature matches what is sent */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Box
          dangerouslySetInnerHTML={{ __html: previewHtml }}
          sx={{ "& img": { maxWidth: "100%" } }}
        />
      )}
    </Box>
  );
}
